/**
 * 3개 좌표(A-B-C)에서 점 B를 꼭짓점으로 하는 사잇각(도, 0~180)을 계산합니다.
 * 화면 종횡비(aspectRatio = width / height)를 반영하여 시각적 각도 왜곡을 보정합니다.
 */
export function getAngle(a, b, c, aspectRatio = 1) {
  if (!a || !b || !c) return 0;

  const ab = { x: (a.x - b.x) * aspectRatio, y: a.y - b.y };
  const cb = { x: (c.x - b.x) * aspectRatio, y: c.y - b.y };

  const dot = ab.x * cb.x + ab.y * cb.y;
  const magAB = Math.sqrt(ab.x * ab.x + ab.y * ab.y);
  const magCB = Math.sqrt(cb.x * cb.x + cb.y * cb.y);

  if (magAB === 0 || magCB === 0) return 0;

  const cosAngle = Math.max(-1, Math.min(1, dot / (magAB * magCB)));
  return Math.round(Math.acos(cosAngle) * (180 / Math.PI));
}

/**
 * 33개 랜드마크에서 양궁 주요 관절 각도(당김팔, 받침팔, 앵커각, 지탱각, 상체 수직도)를 계산합니다.
 * @param {Array} landmarks - 33개 랜드마크 좌표
 * @param {number} width - 캔버스/영상 너비
 * @param {number} height - 캔버스/영상 높이
 * @param {string} dominantHand - 'right' (우측 기준: 당김 12·14·16, 받침 11·13·15) | 'left' (좌측 기준: 당김 11·13·15, 받침 12·14·16)
 */
export function calculateJointAngles(landmarks, width = 1, height = 1, dominantHand = 'right') {
  if (!landmarks || landmarks.length < 25) {
    return {
      drawElbowAngle: 0,
      bowArmAngle: 0,
      torsoAngle: 90,
      angleSupportArm: 0,
      angleDrawArm: 0,
      angle16_12_24: 0,
      angle13_11_23: 0,
      dominantHand,
    };
  }

  const aspectRatio = width > 0 && height > 0 ? width / height : 1;
  const isLeftHanded = dominantHand === 'left';

  // [관절 번호 기준 매핑]
  // 1. 우측 기준 ('right', 기본값):
  //    - 당김팔 / 앵커 (우측): 12(어깨) - 14(팔꿈치) - 16(손목), 24(골반)
  //    - 받침팔 / 지탱 (좌측): 11(어깨) - 13(팔꿈치) - 15(손목), 23(골반)
  // 2. 좌측 기준 ('left'):
  //    - 당김팔 / 앵커 (좌측): 11(어깨) - 13(팔꿈치) - 15(손목), 23(골반)
  //    - 받침팔 / 지탱 (우측): 12(어깨) - 14(팔꿈치) - 16(손목), 24(골반)

  const supportShoulder = isLeftHanded ? landmarks[12] : landmarks[11];
  const supportElbow = isLeftHanded ? landmarks[14] : landmarks[13];
  const supportWrist = isLeftHanded ? landmarks[16] : landmarks[15];
  const supportHip = isLeftHanded ? landmarks[24] : landmarks[23];

  const drawShoulder = isLeftHanded ? landmarks[11] : landmarks[12];
  const drawElbow = isLeftHanded ? landmarks[13] : landmarks[14];
  const drawWrist = isLeftHanded ? landmarks[15] : landmarks[16];
  const drawHip = isLeftHanded ? landmarks[23] : landmarks[24];

  // 받침 팔 들림 각도 (엘보-숄더-힙)
  const angleSupportArm = getAngle(supportElbow, supportShoulder, supportHip, aspectRatio);
  // 당김 팔 들림 각도 (엘보-숄더-힙)
  const angleDrawArm = getAngle(drawElbow, drawShoulder, drawHip, aspectRatio);

  // 지탱 각도 (받침팔 팔꿈치 펴짐도: 숄더-엘보-손목)
  const bowArmAngle = getAngle(supportShoulder, supportElbow, supportWrist, aspectRatio);
  // 앵커 각도 (당김팔 팔꿈치 굽힘각: 숄더-엘보-손목)
  const drawElbowAngle = getAngle(drawShoulder, drawElbow, drawWrist, aspectRatio);

  // 몸통 수직 각도: 어깨 중심과 골반 중심의 기울기 (종횡비 반영)
  const shoulderMid = {
    x: (landmarks[11].x + landmarks[12].x) / 2,
    y: (landmarks[11].y + landmarks[12].y) / 2,
  };
  const hipMid = {
    x: (landmarks[23].x + landmarks[24].x) / 2,
    y: (landmarks[23].y + landmarks[24].y) / 2,
  };

  const dx = (shoulderMid.x - hipMid.x) * aspectRatio;
  const dy = hipMid.y - shoulderMid.y; // 캔버스 y축 반전 고려
  const torsoAngle = Math.round((Math.atan2(dy, dx) * 180) / Math.PI);

  return {
    drawElbowAngle,
    bowArmAngle,
    torsoAngle,
    angleSupportArm,
    angleDrawArm,
    angle16_12_24: isLeftHanded ? angleSupportArm : angleDrawArm,
    angle13_11_23: isLeftHanded ? angleDrawArm : angleSupportArm,
    dominantHand,
  };
}
